import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { YOUTH_PATHWAY } from "../data/youthPathway";

// Reusable "Pathway Badge" — surfaced on /kids and /journey to position the
// Youth Dance Theatre Company as the natural progression for committed
// young dancers. All copy lives in /app/frontend/src/data/youthPathway.js.
//
// Props (all optional — fall back to YOUTH_PATHWAY.badge):
//   eyebrow, title, description, cta, to, testId
//
// Visual style: cinematic dark gradient panel with a "spotlight" yellow halo,
// purple-edged border + scanline accent. Mobile responsive.
export const PathwayBadge = ({
  eyebrow,
  title,
  description,
  cta,
  to,
  testId = "pathway-badge",
}) => {
  const b = YOUTH_PATHWAY.badge;
  const _eyebrow = eyebrow ?? b.eyebrow;
  const _title = title ?? b.title;
  const _description = description ?? b.description;
  const _cta = cta ?? b.cta;
  const _to = to ?? b.to;

  return (
    <section
      data-testid={testId}
      className="relative py-12 sm:py-14 bg-black"
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
        >
          <Link
            to={_to}
            data-testid={`${testId}-banner`}
            className="group relative block overflow-hidden rounded-3xl border border-brand-yellow/30 bg-gradient-to-br from-brand-purpleDeep/45 via-zinc-950 to-black p-7 sm:p-10 cursor-pointer transition-transform duration-300 hover:-translate-y-0.5 hover:border-brand-yellow/60"
          >
          {/* Spotlight halo */}
          <div className="absolute -top-24 -right-16 w-[420px] h-[420px] rounded-full bg-brand-yellow/15 blur-[110px] pointer-events-none" />
          <div className="absolute -bottom-20 -left-10 w-[300px] h-[300px] rounded-full bg-brand-purpleLight/25 blur-[110px] pointer-events-none" />
          {/* Top scanline */}
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/60 to-transparent" />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 mb-4">
                {/* Pulse dot */}
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-brand-yellow opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-brand-yellow" />
                </span>
                <span
                  data-testid={`${testId}-eyebrow`}
                  className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.32em] text-brand-yellow"
                >
                  {_eyebrow}
                </span>
                <span className="hidden sm:inline-flex items-center gap-1.5 ml-3 px-2.5 py-1 rounded-full border border-brand-purpleLight/40 text-[9px] font-bold uppercase tracking-[0.25em] text-brand-purpleLight">
                  <Sparkles size={10} /> Performance Company
                </span>
              </div>
              <h3
                data-testid={`${testId}-title`}
                className="font-display text-3xl sm:text-4xl lg:text-5xl text-white tracking-wide leading-[0.95]"
              >
                {_title}
                <span className="text-brand-yellow text-glow-yellow">.</span>
              </h3>
              <p
                data-testid={`${testId}-description`}
                className="mt-4 max-w-xl text-sm sm:text-base text-zinc-300/90 leading-relaxed"
              >
                {_description}
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <span
                data-testid={`${testId}-cta`}
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider group-hover:bg-brand-yellowHover transition-all glow-yellow"
              >
                {_cta}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </div>
          </div>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
